import React from 'react'
import styled from 'styled-components'
import { roundWithLocale } from '@sketch/utils'
import copy from './copy'
import BorderOptionsSection from './BorderOptionsSection'
import {
  AttributeList,
  FullCopyAttribute,
  Header,
  SubTitle,
  Color as ColorComponent,
  GradientAttribute,
} from '../../../components'
import { getDirtyAttributes } from '../../../components/Style/DirtyIconTooltip'
import {
  Border,
  BorderOptions,
  FillType,
  Gradient,
  Color,
} from 'modules/shares/ArtboardDetailView/hooks/inspector/sketchSceneLib'
import { ColorFormat } from 'modules/shares/types'

const Capitalized = styled.span`
  text-transform: capitalize;
`

export interface BordersProps {
  borders: Border[]
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
  borderOptions?: BorderOptions
  originalValues?: Border[]
  originalValuesOptions?: BorderOptions
}

const isBlendModeVisible = (blendMode: string) =>
  blendMode.toUpperCase() !== 'NORMAL'

interface renderBorderProps extends Border {
  originalValue?: Border
}

const renderBorder = (
  {
    isEnabled,
    fillType,
    gradient,
    color,
    position,
    thickness,
    appearance,
    originalValue,
  }: renderBorderProps,
  {
    onColorFormatChange,
    colorFormat,
  }: {
    onColorFormatChange: (f: ColorFormat) => void
    colorFormat: ColorFormat
  },
  i: number
) => {
  if (!isEnabled) {
    return null
  }

  const blendMode = appearance && appearance.blendMode
  let colorOrGradient = null
  if (fillType === FillType.Color && color) {
    colorOrGradient = (
      <ColorComponent
        {...color}
        onColorFormatChange={onColorFormatChange}
        colorFormat={colorFormat}
        dirtyAttributes={
          originalValue?.color
            ? {
                originalValue: originalValue?.color as Color,
                originalProperty: 'color',
              }
            : undefined
        }
      />
    )
  } else if (fillType === FillType.Gradient && gradient) {
    colorOrGradient = (
      <GradientAttribute
        gradient={gradient}
        onColorFormatChange={onColorFormatChange}
        colorFormat={colorFormat}
        dirtyAttributes={
          originalValue?.gradient
            ? {
                originalGradientValue: originalValue?.gradient as Gradient,
              }
            : undefined
        }
      />
    )
  }

  return (
    <AttributeList key={`InspectorBorder${i}`} data-testid="border">
      {colorOrGradient}
      {isBlendModeVisible(blendMode) ? (
        <FullCopyAttribute
          label="Blend Mode"
          value={<Capitalized>{blendMode}</Capitalized>}
          copyValue={blendMode}
          dirtyAttributes={getDirtyAttributes({
            originalValueKey: 'blendMode',
            valueToDisplay: originalValue?.appearance.blendMode,
            originalValues: originalValue,
            labelToDisplay: 'Blend Mode',
          })}
        />
      ) : null}
      {position ? (
        <FullCopyAttribute
          label="Position"
          value={<Capitalized>{position}</Capitalized>}
          copyValue={position}
          dirtyAttributes={getDirtyAttributes({
            originalValueKey: 'position',
            valueToDisplay: originalValue?.position,
            originalValues: originalValue,
          })}
        />
      ) : null}
      <FullCopyAttribute
        label="Width"
        value={`${roundWithLocale(thickness, 2)}`}
        copyValue={`${thickness}`}
        dirtyAttributes={getDirtyAttributes({
          originalValueKey: 'thickness',
          valueToDisplay: `${roundWithLocale(
            originalValue?.thickness ?? 0,
            2
          )}`,
          originalValues: originalValue,
          labelToDisplay: 'Width',
        })}
      />
    </AttributeList>
  )
}

export const Borders = ({
  borders,
  onColorFormatChange,
  colorFormat,
  borderOptions,
  originalValues,
  originalValuesOptions,
}: BordersProps) => {
  const renderedBorders: Array<JSX.Element> = []
  const reversedBorders = [...borders].reverse()
  const reversedOriginalValues = originalValues
    ? [...originalValues].reverse()
    : undefined

  const dirtyHeaderAttributes =
    originalValues && originalValues[0] === null
      ? { originalValue: 'disabled', originalProperty: 'Borders value' }
      : undefined

  reversedBorders.forEach((border, index) => {
    const renderedBorder = renderBorder(
      {
        ...border,
        originalValue: reversedOriginalValues
          ? reversedOriginalValues[index]
          : undefined,
      },
      {
        onColorFormatChange,
        colorFormat,
      },
      index
    )
    renderedBorder && renderedBorders.push(renderedBorder)
  })

  const sectionCopyValue = copy(reversedBorders, colorFormat)

  return renderedBorders.length > 0 ? (
    <>
      <Header
        copyValue={sectionCopyValue}
        dirtyAttributes={dirtyHeaderAttributes}
      >
        <SubTitle>Borders</SubTitle>
      </Header>
      {renderedBorders}
      {borderOptions && (
        <BorderOptionsSection
          borderOptions={borderOptions}
          originalValues={originalValuesOptions}
        />
      )}
    </>
  ) : null
}
